import { useEffect, useRef } from 'react';
import { useLoaderData } from 'react-router';

import { cn } from '@rocco/utils/cn';
import type { loader } from '~/routes/_app.project.$urlSegment._index';

/**
 * How much slower than the page the mark travels. Below 1 it lags the scroll,
 * which is what reads as depth — it sits behind the page rather than on it.
 */
const PARALLAX_RATE = 0.35;

/**
 * How far it may drift before it holds. The band it sits in is finite, and an
 * unbounded drift would carry the mark down into the sections below, where it
 * would sit behind copy it was never composed against.
 */
const PARALLAX_MAX = 260;

/**
 * The practice's name, very large and very soft, behind the band under the
 * banner.
 *
 * Black on the page's near-black ground, so it reads as a texture in the
 * surface rather than a word set on it — the blur is what keeps it from being
 * read as a heading. It was the ArchiPro "A", which said whose platform this
 * is on a page about whose building it is; the name says the second.
 *
 * Centred on the page rather than held to one side: a name is a different
 * shape from a letterform, and the summary copy it used to clear runs over it
 * softly enough at this blur that the two do not fight.
 *
 * The transform is written straight to the node inside a rAF rather than held
 * in state: this runs on every scroll frame, and a re-render per frame would
 * cost far more than the paint.
 */
export const ProjectBackdropMark = () => {
    const { project } = useLoaderData<typeof loader>();
    const ref = useRef<HTMLDivElement>(null);
    const name = project.Professional?.Title;

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        let frame = 0;
        const apply = () => {
            frame = 0;
            // Measured from the band's own position, not the document's: the
            // drift should start when the band arrives, not carry whatever the
            // page had already scrolled before it came into view.
            const top = el.parentElement?.getBoundingClientRect().top ?? 0;
            const travelled = Math.max(0, window.innerHeight - top);
            const drift = Math.min(travelled * PARALLAX_RATE, PARALLAX_MAX);
            // The -50% carries the horizontal centring: this transform is
            // written on every frame, so a `-translate-x-1/2` class would be
            // overwritten the first time the reader scrolled.
            el.style.transform = `translate3d(-50%, ${drift}px, 0)`;
        };
        const onScroll = () => {
            if (frame) return;
            frame = requestAnimationFrame(apply);
        };

        apply();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            if (frame) cancelAnimationFrame(frame);
        };
    }, [name]);

    if (!name) return null;

    return (
        <div
            ref={ref}
            aria-hidden="true"
            className={cn(
                // Behind the band's content, but not behind the page. A
                // negative z-index would drop it under `main`'s own dark
                // background and it would simply never appear — so the mark
                // takes z-0, the band's content takes z-10, and the sections
                // below the band sit on a z-10 layer of their own.
                'pointer-events-none absolute z-0 will-change-transform max-md:hidden',
                // Centred on the page. The X half of the centring lives in the
                // transform, not here — see the rAF above.
                'top-[-6rem] left-1/2 w-[92vw]',
            )}
            // The initial position, before the first scroll frame writes one.
            style={{ transform: 'translate3d(-50%, 0, 0)' }}
        >
            <p className="m-0 text-center font-sans leading-[0.9] font-medium tracking-[-0.03em] text-black uppercase blur-[10px] md:text-[9rem]">
                {name}
            </p>
        </div>
    );
};
